import axios from "axios";
import React, { useState } from "react";
import { AiFillHeart, AiOutlineHeart } from "react-icons/ai";
import { FiShoppingCart } from "react-icons/fi";
import { TbAdjustmentsHorizontal } from "react-icons/tb";
import { NavLink } from "react-router-dom";
import Swal from "sweetalert2";
import Empty from "../../assets/images/appImages/category.png";
import { useAppDispatch, useAppSelector } from "../../redux/hook";
import {
  addCartProduct,
  getCartCount,
  getTotalAmount,
  removeCartItem,
  type CartItemType,
} from "../../redux/slice/useCartSlice";
import { getImage, truncateCaractere } from "../../utils/Utils";
import {
  getCardPriceDisplay,
  getDefaultCartVariation,
  getProductPromoRate,
  getUnitPriceForVariation,
  needsSizePickerOnCard,
} from "../../utils/productPricing";
import { useLazyFavoriteByUserQuery } from "../../utils/api/favories/favorie.api";
import { useGetVendeurByApiKeyQuery } from "../../utils/api/product/product.api";
import { ApiBaseUrl, VendeurApikey } from "../../utils/http";
import { Color } from "../../utils/theme";
import { ChooseSizeModal } from "./ChooseSizeModal";

interface BestSellerCardProps {
  item: any;
  onFavoriteToggle?: () => void;
}

function stripHtml(html?: string): string {
  if (!html) return "";
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

export const BestSellerCard: React.FC<BestSellerCardProps> = ({
  item,
  onFavoriteToggle,
}) => {
  const dispatch = useAppDispatch();
  const { user, token } = useAppSelector((s) => s?.user);
  const { cartItems } = useAppSelector((s) => s?.cart);
  const { data: vendeur } = useGetVendeurByApiKeyQuery(VendeurApikey);
  const Vendeur = vendeur || { devise: "XOF" };

  const [showSizePicker, setShowSizePicker] = useState(false);

  const imgProd =
    item?.type === "taille_unique"
      ? item?.images?.[0]?.image
      : item?.variations?.[0]?.images?.[0]?.image ?? null;

  const config = {
    headers: { Authorization: `Bearer ${token}` },
  };

  const [findBySlug, { data, isLoading }] = useLazyFavoriteByUserQuery();

  React.useEffect(() => {
    if (user?.slug) {
      findBySlug(user?.slug as string);
    }
  }, [user?.slug, findBySlug]);

  const handleAddFavoris = (id: number | string) => {
    const favorie = data?.find((x) => x?.produit?.id == id);
    const fd = new FormData();
    fd.append("user", String(user?.id ?? ""));
    fd.append("produit", String(id));

    if (favorie) {
      axios
        .delete(ApiBaseUrl + `/api/favoris/${favorie?.slug}`, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((err) => console.error(err));
    } else {
      axios
        .post(ApiBaseUrl + "/api/favoris/", fd, config)
        .then(() => {
          findBySlug(user?.slug as string);
          onFavoriteToggle?.();
        })
        .catch((err) => console.error(err));
    }
  };

  const isFavorite = (id: number | string): boolean =>
    !!data?.find((x) => x?.produit?.id == id);

  const onToggleFavorite = (id: number | string) => {
    if (!user?.slug) {
      Swal.fire({
        icon: "info",
        title: "Veuillez vous connecter pour ajouter un produit aux favoris.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 6000,
      });
    } else {
      handleAddFavoris(id);
    }
  };

  const reductionTaux =
    item?.categorie?.promotion?.taux ||
    item?.sous_categorie?.promotion?.taux ||
    item?.promotion?.taux;

  const hasPromotion =
    item?.categorie?.promotion?.is_active ||
    item?.sous_categorie?.promotion?.is_active ||
    item?.promotion?.is_active;

  const promoRate = getProductPromoRate(item);
  const priceDisplay = getCardPriceDisplay(item, Vendeur?.devise);
  const showSizePickerUI = needsSizePickerOnCard(item);

  const defaultVariation = getDefaultCartVariation(item);
  const cartVariations: any[] = defaultVariation ? [defaultVariation] : [];
  const prixFinal = getUnitPriceForVariation(item, defaultVariation, promoRate);

  const isInCart = (): boolean =>
    cartItems?.some(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) ===
          (cartVariations?.[0]?.id ?? null),
    ) ?? false;

  const getCartItemForProduct = (): CartItemType | undefined =>
    cartItems?.find(
      (cartItem: CartItemType) =>
        cartItem?.produit?.id === item?.id &&
        (cartItem?.variations?.[0]?.id ?? null) ===
          (cartVariations?.[0]?.id ?? null),
    );

  const openSizePicker = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setShowSizePicker(true);
  };

  const addToCartDirect = () => {
    if (!defaultVariation?.id && item?.variations?.length > 0) {
      Swal.fire({
        icon: "warning",
        title: "Variation indisponible",
        text: "Ce produit n'est pas disponible pour le moment.",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 3000,
      });
      return;
    }

    const produit: CartItemType = {
      produit: item,
      prix: prixFinal,
      quantite: 1,
      stock:
        typeof item?.stock === "number"
          ? item.stock
          : Number(item?.stock) || 0,
      variations: cartVariations,
      vendeur: item?.vendeur?.id,
    };

    dispatch(addCartProduct(produit));
    dispatch(getCartCount());
    dispatch(getTotalAmount());
    Swal.fire({
      icon: "success",
      iconColor: Color.success,
      confirmButtonColor: Color.success,
      title: "Produit ajouté au panier avec succès !",
      showConfirmButton: false,
      timer: 2000,
    });
  };

  const handleQuickAddToCart = (e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();

    const stockNum = Number(item?.stock);
    if (stockNum <= 0) return;

    if (showSizePickerUI) {
      openSizePicker(e);
      return;
    }

    if (isInCart()) {
      const cartItem = getCartItemForProduct();
      if (cartItem) {
        dispatch(removeCartItem(cartItem));
        dispatch(getCartCount());
        dispatch(getTotalAmount());
        Swal.fire({
          icon: "info",
          iconColor: Color.success,
          confirmButtonColor: Color.success,
          title: "Produit retiré du panier",
          showConfirmButton: false,
          timer: 2000,
        });
      }
      return;
    }

    addToCartDirect();
  };

  const stockNum = Number(item?.stock);
  const inCart = !showSizePickerUI && isInCart();
  const shortDesc = truncateCaractere(stripHtml(item?.description), 55);

 return (
    <div className="best-seller-card">
      <div className="best-seller-card-image-wrapper">
        {priceDisplay.showMultipleSizesBadge && (
          <div className="best-seller-card-badge-sizes">Plusieurs tailles</div>
        )}

        {hasPromotion && reductionTaux && !priceDisplay.showMultipleSizesBadge && (
          <div className="best-seller-card-badge-left">-{reductionTaux}%</div>
        )}

        <img
          loading="lazy"
          src={imgProd ? getImage(imgProd) : Empty}
          alt={item?.nom ?? ""}
          className="best-seller-card-image"
        />

        <NavLink
          to={`/produit/${item?.slug}`}
          state={item}
          className="best-seller-card-image-overlay"
          aria-label={item?.nom}
        />
      </div>

      <NavLink
        to={`/produit/${item?.slug}`}
        state={item}
        className="best-seller-card-body-link"
      >
        <div className="best-seller-card-body">
          <h4 className="best-seller-card-name">{item?.nom}</h4>

          {shortDesc && (
            <p className="best-seller-card-desc">{shortDesc}</p>
          )}

          <div className="best-seller-card-price">
            <span
              className={`best-seller-card-price-final ${priceDisplay.startingFrom ? "best-seller-card-price-final--from" : ""}`}
            >
              {priceDisplay.display}
            </span>
            {priceDisplay.strikethrough && (
              <span className="best-seller-card-price-original">
                {priceDisplay.strikethrough}
              </span>
            )}
          </div>
        </div>
      </NavLink>

      <div className="best-seller-card-button-wrapper">
        {stockNum > 0 ? (
          showSizePickerUI ? (
            <button
              type="button"
              className="best-seller-card-button best-seller-card-button--choose-size"
              onClick={openSizePicker}
            >
              <TbAdjustmentsHorizontal />
              Choisir la taille
            </button>
          ) : (
            <button
              type="button"
              className={`best-seller-card-button best-seller-card-button--add-cart ${inCart ? "best-seller-card-button--in-cart" : ""}`}
              onClick={handleQuickAddToCart}
            >
              Acheter
            </button>
          )
        ) : (
          <button
            type="button"
            className="best-seller-card-button best-seller-card-button-disabled"
            disabled
          >
            Rupture de stock
          </button>
        )}

        {/* Icône panier séparée à côté du bouton, comme sur la maquette */}
        {stockNum > 0 && !showSizePickerUI && (
          <button
            type="button"
            className={`best-seller-card-cart-icon ${inCart ? "best-seller-card-cart-icon-in-cart" : ""}`}
            aria-label={inCart ? "Retirer du panier" : "Ajouter au panier"}
            onClick={handleQuickAddToCart}
          >
            <FiShoppingCart />
          </button>
        )}

        {/* Icône favoris à côté du panier */}
        <button
          type="button"
          disabled={isLoading}
          className="best-seller-card-favorite-inline"
          aria-label="Ajouter aux favoris"
          onClick={() => onToggleFavorite(item?.id)}
        >
          {isFavorite(item?.id) ? (
            <AiFillHeart className="best-seller-card-favorite-icon-filled" />
          ) : (
            <AiOutlineHeart className="best-seller-card-favorite-icon" />
          )}
        </button>
      </div>

      {showSizePickerUI && (
        <ChooseSizeModal
          product={item}
          devise={Vendeur?.devise}
          imageUrl={imgProd}
          isOpen={showSizePicker}
          onClose={() => setShowSizePicker(false)}
        />
      )}
    </div>
  );
};
